import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Video, Users } from "lucide-react";
import { subscribePulse } from "../utils/pulse";
import useFeatures from "../hooks/useFeatures";
import { playNotificationTone } from "../utils/notificationSound";

// Violet pill before the bells when one of my supervision meetings starts
// within 15 minutes or is under way (the "supervision" part of the shared
// poll, utils/pulse.js).
// It chimes and shows a desktop notification (when the browser allows it)
// once when the meeting first appears, again 10 minutes before, and again at
// the start - so a clinician on another tab or with a patient still notices.
// Clicking opens the request, and joins the call when it's a video meeting.
//
// Module-level store, like CrisisRibbon: the layout renders two copies and
// every page mounts its own layout.
const SEEN_KEY = "supervision_ribbon_alerted";

const store = { list: [], listeners: new Set(), unsub: null, alerted: new Set() };
try { JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]").forEach((k) => store.alerted.add(k)); } catch { /* none */ }

function alertOnce(key, title, body) {
  if (store.alerted.has(key)) return;
  store.alerted.add(key);
  try { sessionStorage.setItem(SEEN_KEY, JSON.stringify([...store.alerted])); } catch { /* private mode */ }
  playNotificationTone();
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      new Notification(title, { body, tag: key });
    }
  } catch { /* not supported */ }
}

// Fed by the shared background poll (utils/pulse.js, GET /pulse "supervision").
function onPulse(p) {
  if (!Array.isArray(p.supervision)) return;
  store.list = p.supervision;
  store.listeners.forEach((fn) => fn(store.list));
  store.list.forEach((m) => {
    const who = m.with_name || "your colleague";
    const how = m.can_join ? "Join from the Supervision page." : m.mode === "phone" ? "By phone." : "In person.";
    alertOnce(`${m.request_id}-${m.starts_at}-soon`, `Supervision with ${who} in ${Math.max(m.minutes_to_start, 0)} min`, how);
    if (m.minutes_to_start <= 10) alertOnce(`${m.request_id}-${m.starts_at}-10`, `Supervision with ${who} in ${Math.max(m.minutes_to_start, 0)} min`, how);
    if (m.minutes_to_start <= 0) alertOnce(`${m.request_id}-${m.starts_at}-start`, `Supervision with ${who} is starting`, how);
  });
}

function subscribe(fn) {
  store.listeners.add(fn);
  fn(store.list);
  if (!store.unsub) store.unsub = subscribePulse(onPulse);
  return () => {
    store.listeners.delete(fn);
    if (store.listeners.size === 0) { store.unsub(); store.unsub = null; }
  };
}

// Ask for desktop notifications from a click (browsers only allow it then).
export function askForMeetingNotifications() {
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "default") Notification.requestPermission();
  } catch { /* not supported */ }
}

export default function SupervisionRibbon({ compact = false }) {
  const features = useFeatures();
  const navigate = useNavigate();
  const [list, setList] = useState(store.list);
  useEffect(() => {
    if (!features.has("supervision")) return undefined;
    return subscribe((l) => setList([...l]));
  }, [features.ready]); // eslint-disable-line

  if (list.length === 0) return null;
  const m = list[0];
  const when = m.minutes_to_start > 0 ? `in ${m.minutes_to_start} min` : "now";
  const Icon = m.can_join ? Video : Users;
  return (
    <button type="button" onClick={() => navigate(`/supervision?id=${m.request_id}${m.can_join ? "&join=1" : ""}`)}
      title={`Supervision with ${m.with_name || "your colleague"} ${when}`}
      className={`relative inline-flex items-center gap-1.5 rounded-full bg-violet-700 font-bold text-white shadow-lg shadow-violet-700/40 ${compact ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm"}`}>
      {m.minutes_to_start <= 10 && <span className="absolute inset-0 rounded-full ring-4 ring-violet-500/50 animate-ping pointer-events-none" aria-hidden="true" />}
      <Icon className="w-4 h-4" />
      {compact ? when : <>Supervision with {m.with_name || "colleague"} {when}{m.can_join ? <> · <span className="underline">Join</span></> : ""}</>}
    </button>
  );
}
